var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { LoginAction, LogoutAction } from "../../redux/actions/authentication";
import Button from "../atoms/button/button";
import LeftCoverImage from "../atoms/leftCoverImage/leftCoverImage";
import InputField from "../molecules/inputField/inputField";
import BrandHeader from "../molecules/brandHeader/brandHeader";
import "../styles/main.css";
import "../styles/login.css";
import LoginTemplate from "../templates/loginTemplate";
import ErrorMessage from "../atoms/errorMessage/errorMessage";
import { validateFormData } from "./questionnaire/questionnaires/validationUtils";
// Eye Icons as SVG components
var EyeIcon = function () { return (_jsxs("svg", __assign({ width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }, { children: [_jsx("path", { d: "M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" }), _jsx("circle", { cx: "12", cy: "12", r: "3" })] }))); };
var EyeOffIcon = function () { return (_jsxs("svg", __assign({ width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }, { children: [_jsx("path", { d: "M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" }), _jsx("line", { x1: "1", y1: "1", x2: "23", y2: "23" })] }))); };
/**
 * The Login Page
 * This will need to call all relevant APIs to get the values.
 * @param props
 * @returns
 */
function Login() {
    var navigate = useNavigate();
    var dispatch = useDispatch();
    // Logout if the sessionStorage says so.
    var needsLogout = sessionStorage.getItem("logout");
    if (needsLogout === "true") {
        // Reset data for later.
        sessionStorage.removeItem("logout");
        dispatch(LogoutAction());
    }
    //using selector hook to fetch data from the reducer
    var userData = useSelector(function (state) { return state.rootReducer.userReducer; });
    // Logout user whenever login component is mounted with an existing session
    useEffect(function () {
        if (userData.loginData) {
            dispatch(LogoutAction());
        }
    }, []);
    //creating react state hook to store username and password locally
    var _a = useState({
        username: "",
        password: "",
    }), loginData = _a[0], setLoginData = _a[1];
    // State for password visibility
    var _b = useState(false), showPassword = _b[0], setShowPassword = _b[1];
    var _c = useState({
        username: "",
        password: "",
        credentials: "",
    }), errorMessage = _c[0], setErrorMessage = _c[1];
    //function to submit the input user data
    var handleSubmit = function (e) {
        var _a;
        e.preventDefault();
        var trimmedData = __assign(__assign({}, loginData), { username: loginData.username.trim() });
        var validation = validateFormData(trimmedData);
        if (!(validation === null || validation === void 0 ? void 0 : validation.isValid)) {
            var errorId = validation === null || validation === void 0 ? void 0 : validation.errorField;
            setErrorMessage(__assign(__assign({}, errorMessage), (_a = {}, _a[errorId] = validation === null || validation === void 0 ? void 0 : validation.errorMessage, _a)));
        }
        else {
            dispatch(LoginAction(trimmedData));
        }
    };
    //function to change the values in the input fields entered by the user
    var handleChange = function (e) {
        var _a;
        var _b = e.target, name = _b.name, value = _b.value;
        setLoginData(__assign(__assign({}, loginData), (_a = {}, _a[name] = value, _a)));
    };
    // Toggle password visibility
    var togglePasswordVisibility = function () {
        setShowPassword(!showPassword);
    };
    //navigation to dashboard page on successful login
    if (userData.loginData) {
        navigate("/dashboard");
    }
    return (_jsx(LoginTemplate, { leftImage: _jsx(LeftCoverImage, {}), form: _jsxs("form", __assign({ onSubmit: handleSubmit, className: "form mx-auto px-4 sm:px-6" }, { children: [_jsx(BrandHeader, {}), _jsx("h1", __assign({ className: "heading mt-5 text-2xl sm:text-3xl font-bold" }, { children: "Login" })), _jsx("div", __assign({ className: "h3 mb-3 mt-1 font-medium text-gray-600 text-sm sm:text-base" }, { children: "Please enter your account details" })), _jsxs("div", __assign({ className: "space-y-4 mt-4" }, { children: [_jsx(InputField, { label: "Email or Username", type: "text", id: "id_username", name: "username", placeholder: "", value: loginData.username, onChange: handleChange, className: "login w-full" }), errorMessage.username ? (_jsx(ErrorMessage, { message: "This field is required*" }, "username")) : (""), _jsxs("div", __assign({ className: "relative" }, { children: [_jsx(InputField, { label: "Password", type: showPassword ? "text" : "password", id: "id_password", name: "password", placeholder: "", value: loginData.password, onChange: handleChange, className: "login w-full" }), _jsx("button", __assign({ type: "button", onClick: togglePasswordVisibility, className: "absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none", style: { top: "calc(50% + 15px)" } }, { children: showPassword ? _jsx(EyeOffIcon, {}) : _jsx(EyeIcon, {}) }))] })), errorMessage.password ? (_jsx(ErrorMessage, { message: "This field is required*" }, "password")) : ("")] })), _jsx("div", __assign({ className: "flex justify-center mt-4 mb-6 text-sm sm:text-base" }, { children: _jsx("a", __assign({ href: "/passwordResetEmail", className: "text-blue-600 hover:text-blue-700 hover:underline" }, { children: "Forgot Password?" })) })), _jsxs("div", __assign({ className: "pt-4 sm:pt-6" }, { children: [_jsx(Button, { border: "none", onClick: function () { return handleSubmit; }, text: "Login", className: "w-full mt-4 sm:mt-6 login-btn" }), userData.error_login && userData.error_login.length > 0 ? (_jsx(ErrorMessage, { message: "".concat(userData.error_login), classname: "text-center" }, "credentials")) : ("")] }))] })) }));
}
export default Login;
